import React, { useEffect, useRef, useState } from "react";
import { FlatList, Pressable, TextInput, View, StyleSheet, Platform } from "react-native";
import Svg, { Path } from "react-native-svg";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ChessRestClient from "@/Api/ChessRestClient";
import CCText from "@/Atoms/CCText";
import useGetChannelMessagesApi from "@/Hooks/GetInboxApi/useGetChannelMessagesApi";
import useSendToChannelApi from "@/Hooks/GetInboxApi/useSendToChannelApi";
import { useDashboardApiQuery } from "@/Hooks/DashboardApi/dashboardApi";
import { useSocket } from "@/Hooks/UseSocket";
import {
  useClubPolls,
  useVotePoll,
  useCreatePoll,
  useClosePoll,
  useClubChallenges,
  useCreateChallenge,
  useAcceptChallenge,
  useCancelChallenge,
} from "@/Hooks/ClubsApi/useClubsQueries";
import { setLocalStorage } from "@/Utils/CommonUtils";
import { buildRejectPacket } from "@/Screens/Clubs/challengeResponse";
import useClubChallengeStore from "@/Store/clubChallengeStore";
import { ScreenName } from "@/navigators/StackConstants";
import PollCard from "./PollCard";
import CreatePollSheet from "./CreatePollSheet";
import ChallengeCard from "./ChallengeCard";
import CreateChallengeSheet from "./CreateChallengeSheet";
import { ChatSkeleton } from "./ClubRoomSkeletons";

// Avatar hash colours — matches the design's per-user tint palette.
const AVATAR_COLORS = ["#3DAB9E", "#E17846", "#67B3E1", "#937ADB", "#D1AB41"];
function avatarColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = hash & hash;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(sender: string): string {
  const parts = (sender || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

type ChatMessage = {
  id: number | string;
  body: string;
  sender?: string | null;
  is_me?: boolean;
  created_at?: string | null;
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// WhatsApp-style day label: "Today" / "Yesterday" / "23 Jul" (this year) / "23 Jul 2025".
function dayLabel(ms: number): string {
  if (!ms) return "";
  const d = new Date(ms);
  const now = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  const base = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base} ${d.getFullYear()}`;
}

// WhatsApp-style short delivered time, e.g. "9:41 AM". Empty string if unknown.
function deliveredTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  list: { paddingHorizontal: 14, paddingTop: 14, paddingBottom: 8 },
  // System line — centered muted text (design lines 348 / 872).
  systemText: {
    textAlign: "center",
    fontSize: 11.5,
    color: "#A8A7A6",
    paddingVertical: 2,
    paddingHorizontal: 20,
  },
  // Other-message row: avatar + (name over bubble), left aligned.
  otherRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    maxWidth: "82%",
    gap: 8,
    marginBottom: 11,
  },
  avatarCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: { fontSize: 11, color: "#FFFFFF", fontWeight: "600" },
  senderName: { fontSize: 11, color: "#888280", marginLeft: 4, marginBottom: 2 },
  otherBubble: {
    borderRadius: 14,
    borderBottomLeftRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 9,
    alignSelf: "flex-start",
  },
  otherBubbleText: { fontSize: 14, color: "#262322" },
  // WhatsApp-style delivered time under each bubble.
  meTime: { fontSize: 10, color: "#888280", alignSelf: "flex-end", marginTop: 2, marginRight: 2 },
  otherTime: { fontSize: 10, color: "#A8A7A6", marginTop: 2, marginLeft: 4 },
  // My-message bubble: aqua, right aligned, no avatar/name. (Alignment/spacing
  // now live on the wrapping column so the delivered time can sit under it.)
  meBubbleInner: {
    alignSelf: "flex-end",
    backgroundColor: "#3DAB9E",
    borderRadius: 14,
    borderBottomRightRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  meBubbleText: { fontSize: 14, color: "#FFFFFF" },
  composerBar: {
    borderTopWidth: 1,
    borderTopColor: "rgba(38,35,34,0.10)",
    paddingTop: 10,
    paddingBottom: 14,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#FAF6EB",
  },
  plusButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(38,35,34,0.20)",
    justifyContent: "center",
    alignItems: "center",
  },
  textInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: "rgba(38,35,34,0.20)",
    borderRadius: 22,
    paddingHorizontal: 15,
    paddingVertical: 11,
    fontSize: 14.5,
    color: "#262322",
  },
  sendButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: "#FACF47",
    justifyContent: "center",
    alignItems: "center",
  },
  composerMenuItem: {
    flex: 1,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(38,35,34,0.10)",
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: "center",
  },
  composerMenuText: { fontSize: 13, fontWeight: "600", color: "#4D3F37" },
  // WhatsApp-style centered day pill.
  dateRow: { alignItems: "center", paddingVertical: 8 },
  datePill: {
    backgroundColor: "#EDE6D2",
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  datePillText: { fontSize: 11, fontWeight: "600", color: "#6B584D" },
});

function DateSeparator({ label }: { label: string }) {
  return (
    <View style={styles.dateRow}>
      <View style={styles.datePill}>
        <CCText style={styles.datePillText}>{label}</CCText>
      </View>
    </View>
  );
}

function Bubble({ item, isWide, isMe }: { item: ChatMessage; isWide: boolean; isMe: boolean }) {
  const hasSender = !!(item.sender && item.sender.trim());
  // No sender → system line (e.g. "X joined the club"). Matches design.
  if (!hasSender && !isMe) {
    return <CCText style={styles.systemText}>{item.body}</CCText>;
  }
  const time = deliveredTime(item.created_at);
  if (isMe) {
    return (
      <View style={{ alignSelf: "flex-end", maxWidth: "82%", marginBottom: 11 }}>
        <View testID="chat-msg-me" style={styles.meBubbleInner}>
          <CCText style={styles.meBubbleText}>{item.body}</CCText>
        </View>
        {time ? <CCText style={styles.meTime}>{time}</CCText> : null}
      </View>
    );
  }
  // Other: desktop uses cream bubble (no shadow); mobile uses white + soft shadow.
  const bubbleSurface = isWide
    ? { backgroundColor: "#FAF6EB" }
    : {
        backgroundColor: "#FFFFFF",
        shadowColor: "#4D3F37",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
      };
  return (
    <View testID="chat-msg-other" style={styles.otherRow}>
      <View style={[styles.avatarCircle, { backgroundColor: avatarColor(item.sender || "") }]}>
        <CCText style={styles.avatarText}>{getInitials(item.sender || "")}</CCText>
      </View>
      <View>
        <CCText style={styles.senderName}>{item.sender}</CCText>
        <View style={[styles.otherBubble, bubbleSurface]}>
          <CCText style={styles.otherBubbleText}>{item.body}</CCText>
        </View>
        {time ? <CCText style={styles.otherTime}>{time}</CCText> : null}
      </View>
    </View>
  );
}

export default function ChatTab({
  clubId,
  isWide = false,
  isAdmin = false,
  navigation,
}: {
  clubId: number;
  isWide?: boolean;
  isAdmin?: boolean;
  navigation?: any;
}) {
  const channelQ = useQuery(
    ["clubs", "channel", clubId],
    async (): Promise<{ channel_id: number }> =>
      (await ChessRestClient.get(`/api/clubs/${clubId}/channel/`)).data
  );
  const channelId = channelQ.data?.channel_id;
  const messagesQ = useGetChannelMessagesApi(channelId as number);
  const send = useSendToChannelApi(channelId as number);
  const [text, setText] = useState("");
  const listRef = useRef<FlatList<any>>(null);

  // ── Older-page pagination (shared timestamp cursor) ──────────────────────────
  // The React-Query hooks below load the NEWEST page (and drive real-time refetch).
  // Scrolling up appends older pages here via a single `before` cursor shared by
  // all three sources. Each older row is {kind, data}; merged with the newest page
  // in the timeline memo (deduped by key).
  const [olderMsgs, setOlderMsgs] = useState<any[]>([]);
  const [olderPolls, setOlderPolls] = useState<any[]>([]);
  const [olderChals, setOlderChals] = useState<any[]>([]);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const loadingOlderRef = useRef(false);

  // Real-time: the game-service socket pushes events to each club member (chess
  // enqueues them to Redis; the game service fans them out). We refetch the
  // relevant query when the event is for this room — same pattern as Inbox.
  //   • inbox_message      → new chat message (match on channel_id)
  //   • club_poll_update   → poll created / voted / closed (match on club_id)
  //   • club_challenge_update → challenge posted / accepted / done / cancelled
  const socket = useSocket();
  const qc = useQueryClient();
  useEffect(() => {
    if (!socket || !channelId) return;
    const parse = (data: any): any => {
      try {
        const first = JSON.parse(data?.message);
        return typeof first === "string" ? JSON.parse(first) : first;
      } catch {
        return null;
      }
    };
    const onInbox = (_s: any, data: any) => {
      const p = parse(data);
      if (p && String(p.channel_id) === String(channelId)) {
        (messagesQ as any).refetchForChannel?.(channelId);
      }
    };
    const onPoll = (_s: any, data: any) => {
      const p = parse(data);
      if (p && String(p.club_id) === String(clubId)) {
        qc.invalidateQueries(["clubs", "polls", clubId]);
      }
    };
    const onChallenge = (_s: any, data: any) => {
      const p = parse(data);
      if (p && String(p.club_id) === String(clubId)) {
        qc.invalidateQueries(["clubs", "challenges", clubId]);
      }
    };
    // Keyed registration (same pattern as Inbox / FunChallenges): re-registering
    // with the same key replaces the prior handler, so no explicit teardown is
    // needed when channelId changes.
    socket.registerMessageHandler?.("inbox_message", onInbox, `ClubChat:${channelId}`);
    socket.registerMessageHandler?.("club_poll_update", onPoll, `ClubPoll:${clubId}`);
    socket.registerMessageHandler?.("club_challenge_update", onChallenge, `ClubChal:${clubId}`);
  }, [socket, channelId, clubId]);

  // Polls — rendered inline in the chat stream (design).
  const pollsQ = useClubPolls(clubId);
  const votePoll = useVotePoll(clubId);
  const createPoll = useCreatePoll(clubId);
  const closePoll = useClosePoll(clubId);
  const [pollSheetOpen, setPollSheetOpen] = useState(false);

  // Challenges — rendered inline in the chat stream (design). Accept/Play route to
  // PlayChess with club challenge keys set, mirroring the CCLeague challenge flow.
  const challengesQ = useClubChallenges(clubId);
  const createChallenge = useCreateChallenge(clubId);
  const acceptChallenge = useAcceptChallenge(clubId);
  const cancelChallenge = useCancelChallenge(clubId);
  const [challengeSheetOpen, setChallengeSheetOpen] = useState(false);
  const [composerMenuOpen, setComposerMenuOpen] = useState(false);

  // The CREATOR is pulled into the game when one of MY challenges TRANSITIONS to
  // "accepted" (real-time club_challenge_update refetch). The creator navigates to
  // PlayChess as role "creator" → PlayChess fires challenge_game_request to the
  // accepter (the proven peer-challenge flow). The accepter, having marked the
  // creator "expected" on tap, then auto-accepts the incoming check. We must NOT
  // auto-navigate on challenges that were ALREADY accepted at first load (stale) —
  // else opening the chat redirects to PlayChess. `firedRef` is seeded on first load.
  const markExpected = useClubChallengeStore((s) => s.markExpected);
  const firedRef = useRef<Set<number>>(new Set());
  const seededRef = useRef(false);
  useEffect(() => {
    const data = (challengesQ as any).data;
    if (!data) return; // wait for the first real fetch
    const list = data.challenges ?? [];

    if (!seededRef.current) {
      seededRef.current = true;
      for (const c of list) {
        if (c.is_mine && c.state === "accepted") firedRef.current.add(c.id);
      }
    }

    for (const c of list) {
      if (
        c.is_mine &&
        c.state === "accepted" &&
        c.opponent_id != null &&
        !c.game_id &&
        !firedRef.current.has(c.id)
      ) {
        firedRef.current.add(c.id);
        // Creator fires the game to the accepter (opponent_id/opponent).
        goToPlayChessForClub(c, "creator", c.opponent_id, c.opponent);
      }
    }
  }, [(challengesQ as any).data]);

  // Resolve the current user the same way CCLeague/Inbox chat does: the app-wide
  // ['dashboard'] query exposes user.username, which we compare to each message's
  // sender to decide "me" vs "other". (React Query cache — no extra request.)
  const dashboardQ = useDashboardApiQuery({});
  const currentUsername: string | undefined = (dashboardQ as any).data?.user?.username;
  const myUserId = (dashboardQ as any).data?.user?.id;


  // A message is mine when its sender matches the current username (Inbox parity).
  // Fall back to the backend `is_me` flag if the username isn't available yet.
  const isMine = (m: ChatMessage): boolean =>
    !!currentUsername && m.sender != null
      ? m.sender === currentUsername
      : !!m.is_me;

  const messages: ChatMessage[] = (messagesQ as any).data?.messages ?? [];
  const polls = (pollsQ as any).data?.polls ?? [];
  const challenges = (challengesQ as any).data?.challenges ?? [];

  const onSend = () => {
    const body = text.trim();
    if (!body) return;
    (send as any).mutate({ body });
    setText("");
  };

  // Navigate to PlayChess for a club game and stash what PlayChess needs to drive the
  // handshake (mirrors the proven peer-challenge flow):
  //   role "creator"  → PlayChess fires challenge_game_request to `oppId:oppName`
  //                     (creator plays White). This is the real challenge.
  //   role "accepter" → PlayChess just waits; the creator's incoming
  //                     challenge_request_check is auto-accepted by the app-wide
  //                     ChallengeComponent (this client already marked the creator
  //                     "expected"). No packet is sent from PlayChess for accepters.
  const goToPlayChessForClub = (
    ch: any,
    role: "creator" | "accepter",
    oppId: number | null | undefined,
    oppName: string | null | undefined,
  ) => {
    if (oppId == null || !oppName) return; // no counterpart → nothing to start
    setLocalStorage("challengePacket", null); // never send a stale peer accept
    setLocalStorage("club_challenge_id", String(ch.id));
    setLocalStorage("club_opp_id", String(oppId));
    setLocalStorage("club_opp_name", String(oppName));
    setLocalStorage("club_challenge_tc", String(ch.time_control || "5+0"));
    setLocalStorage("club_challenge_role", role);
    // Remember which club to return to when the game's end-game modal is closed.
    // Persisted separately (NOT cleared by clearClubChallengeKeys) so it survives
    // the game and drives the back-navigation on close.
    setLocalStorage("club_return_id", String(clubId));
    navigation?.navigate?.(ScreenName.PlayChess);
  };

  // Build the bare reject packet payload from a REST challenge row (creator = White).
  const syntheticFromChallenge = (ch: any) => ({
    requestId: String(Date.now()),
    variantId: 1,
    opponent: String(ch.created_by_id), // bare id
    playerMoveTimer: 300000,
    moveTimeIncrement: 0,
    gameState: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    white: String(ch.created_by_id),
    gamePlayMode: "Play",
  });

  // Scroll-up pagination: fetch the next older page of all three sources using the
  // shared `before` cursor (oldest item currently loaded). Appends to the older-*
  // accumulators. Stops when every source returns nothing.
  const PAGE = 10;
  const loadOlder = async () => {
    if (loadingOlderRef.current || !hasMoreOlder || !channelId || !oldestAt) return;
    loadingOlderRef.current = true;
    const before = new Date(oldestAt).toISOString();
    try {
      const [mRes, pRes, cRes] = await Promise.all([
        ChessRestClient.get(`/inbox/get_messages/${channelId}`, {
          params: { before, limit: PAGE },
        }),
        ChessRestClient.get(`/api/clubs/${clubId}/polls/`, {
          params: { before, limit: PAGE },
        }),
        ChessRestClient.get(`/api/clubs/${clubId}/challenges/`, {
          params: { before, limit: PAGE },
        }),
      ]);
      const newMsgs = mRes?.data?.messages ?? [];
      const newPolls = pRes?.data?.polls ?? [];
      const newChals = cRes?.data?.challenges ?? [];
      if (newMsgs.length) setOlderMsgs((prev) => [...prev, ...newMsgs]);
      if (newPolls.length) setOlderPolls((prev) => [...prev, ...newPolls]);
      if (newChals.length) setOlderChals((prev) => [...prev, ...newChals]);
      if (!newMsgs.length && !newPolls.length && !newChals.length) {
        setHasMoreOlder(false);
      }
    } catch (e) {
      // Leave hasMoreOlder true so a later scroll can retry.
      console.log("club chat loadOlder failed", (e as any)?.message);
    } finally {
      loadingOlderRef.current = false;
    }
  };

  // Merge newest-page + older-page items, dedupe, sort DESCENDING (newest first)
  // for an INVERTED list — newest sits at the visual bottom, and onEndReached fires
  // when the user scrolls to the top (oldest) to load more. Day-separator rows are
  // interleaved so each calendar day is labelled (WhatsApp-style).
  const { rows, oldestAt } = React.useMemo(() => {
    const at = (v?: string | null) => {
      const t = v ? new Date(v).getTime() : NaN;
      return isNaN(t) ? 0 : t;
    };
    const byKey = new Map<string, { kind: "msg" | "poll" | "chal"; key: string; sortAt: number; data: any }>();
    const add = (kind: "msg" | "poll" | "chal", prefix: string, arr: any[]) => {
      for (const it of arr) {
        const key = `${prefix}-${it.id}`;
        // Newest-page data wins over older-page copies (fresher poll/challenge state).
        if (!byKey.has(key)) byKey.set(key, { kind, key, sortAt: at(it.created_at), data: it });
      }
    };
    // Newest-page sources first (so they take precedence on dedupe). The shared
    // messages hook returns up to 50; cap the NEWEST page to 10 so the first load
    // shows the last ~10 combined. Older messages (incl. the capped-off 11..50)
    // reveal on scroll-up via loadOlder (the Map dedupes any overlap).
    const newestMsgs = messages.slice(0, 10);
    add("msg", "m", newestMsgs);
    add("poll", "p", polls);
    add("chal", "c", challenges);
    add("msg", "m", olderMsgs);
    add("poll", "p", olderPolls);
    add("chal", "c", olderChals);

    const items = Array.from(byKey.values());
    // Descending (newest first) for the inverted list.
    items.sort((a, b) => b.sortAt - a.sortAt || b.key.localeCompare(a.key));

    const oldest = items.length ? items[items.length - 1].sortAt : 0;

    // Interleave day separators. Items are newest→oldest (inverted list order). A
    // date row is emitted immediately AFTER the last item of each day (i.e. right
    // before the first item of the next, older, day) plus once at the very end for
    // the oldest day — so in the inverted view each label floats atop its day block.
    const out: Array<
      | { type: "date"; key: string; label: string }
      | { type: "item"; key: string; kind: "msg" | "poll" | "chal"; data: any }
    > = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      out.push({ type: "item", key: it.key, kind: it.kind, data: it.data });
      const dayKey = it.sortAt ? new Date(it.sortAt).toDateString() : "unknown";
      const next = items[i + 1];
      const nextDayKey = next
        ? next.sortAt
          ? new Date(next.sortAt).toDateString()
          : "unknown"
        : null;
      // Day changes to the next (older) item, or this is the last (oldest) item →
      // emit the current item's day label after it.
      if (dayKey !== nextDayKey) {
        out.push({ type: "date", key: `date-${dayKey}-${i}`, label: dayLabel(it.sortAt) });
      }
    }
    return { rows: out, oldestAt: oldest };
  }, [messages, polls, challenges, olderMsgs, olderPolls, olderChals]);

  // Early return AFTER all hooks have run (hooks must be called unconditionally).
  if (!channelId || channelQ.isLoading || (messagesQ as any).isLoading) {
    return <ChatSkeleton />;
  }

  const renderRow = (row: any) => {
    if (row.type === "date") {
      return <DateSeparator label={row.label} />;
    }
    if (row.kind === "msg") {
      return <Bubble item={row.data} isWide={isWide} isMe={isMine(row.data)} />;
    }
    if (row.kind === "chal") {
      const c = row.data;
      return (
        <ChallengeCard
          challenge={c}
          mine={!!c.is_mine}
          onAccept={() =>
            // Accept: record accepted (REST). The CREATOR then fires the game
            // (driven by the real-time accepted signal). This client (accepter) marks
            // the creator "expected" so the incoming challenge_request_check is
            // auto-accepted silently, then waits on PlayChess as role "accepter".
            (acceptChallenge as any).mutate(c.id, {
              onSuccess: (res: any) => {
                const ch = res?.challenge ?? c;
                if (ch.created_by_id != null) markExpected(ch.created_by_id);
                goToPlayChessForClub(ch, "accepter", ch.created_by_id, ch.created_by);
              },
            })
          }
          onReject={
            c.is_mine
              ? undefined
              : () => {
                  socket?.send_msg?.(
                    "challenge_game_request",
                    buildRejectPacket(syntheticFromChallenge(c), String(myUserId)),
                  );
                  cancelChallenge.mutate(c.id);
                }
          }
          onPlay={() =>
            // Play now → creator fires (role creator); a non-creator participant
            // waits as accepter (marks the creator expected first).
            c.is_mine
              ? goToPlayChessForClub(c, "creator", c.opponent_id, c.opponent)
              : (() => {
                  if (c.created_by_id != null) markExpected(c.created_by_id);
                  goToPlayChessForClub(c, "accepter", c.created_by_id, c.created_by);
                })()
          }
          onCancel={c.is_mine ? () => cancelChallenge.mutate(c.id) : undefined}
        />
      );
    }
    // poll — align like a chat message: right if I created it, left otherwise.
    const p = row.data;
    const pollMine = myUserId != null && p.created_by_id === myUserId;
    return (
      <PollCard
        poll={p}
        mine={pollMine}
        onVote={(optionId) => votePoll.mutate({ pollId: p.id, optionId })}
        onClose={() => closePoll.mutate(p.id)}
        canClose={isAdmin || pollMine}
      />
    );
  };

  return (
    <View style={styles.container}>
      <CreatePollSheet
        visible={pollSheetOpen}
        submitting={(createPoll as any).isLoading}
        onCancel={() => setPollSheetOpen(false)}
        onCreate={(question, options) =>
          (createPoll as any).mutate(
            { question, options },
            { onSuccess: () => setPollSheetOpen(false) }
          )
        }
      />
      <CreateChallengeSheet
        visible={challengeSheetOpen}
        submitting={(createChallenge as any).isLoading}
        onCancel={() => setChallengeSheetOpen(false)}
        onCreate={(timeControl, rated) =>
          (createChallenge as any).mutate(
            { time_control: timeControl, rated },
            { onSuccess: () => setChallengeSheetOpen(false) }
          )
        }
      />
      {/* Unified chronological timeline (messages + polls + challenges + day
          separators). INVERTED: `rows` is newest-first, so index 0 shows at the
          visual bottom (newest) and the list stays pinned there on new content.
          Scrolling to the top (end of the data) loads the next older page. */}
      <FlatList
        ref={listRef}
        data={rows}
        inverted={rows.length > 0}
        keyExtractor={(row) => row.key}
        style={{ flex: 1, minHeight: 0 }}
        contentContainerStyle={styles.list}
        onEndReached={() => loadOlder()}
        onEndReachedThreshold={0.3}
        renderItem={({ item }) => renderRow(item)}
      />
      {/* Composer menu (design lines 393–396): Create poll + Post a challenge.
          Both are open to any club member. */}
      {composerMenuOpen ? (
        <View style={{ flexDirection: "row", gap: 8, paddingHorizontal: 12, paddingBottom: 8 }}>
          <Pressable
            testID="composer-create-poll"
            onPress={() => {
              setComposerMenuOpen(false);
              setPollSheetOpen(true);
            }}
            style={styles.composerMenuItem}
          >
            <CCText style={styles.composerMenuText}>📊 Create poll</CCText>
          </Pressable>
          <Pressable
            testID="composer-post-challenge"
            onPress={() => {
              setComposerMenuOpen(false);
              setChallengeSheetOpen(true);
            }}
            style={styles.composerMenuItem}
          >
            <CCText style={styles.composerMenuText}>♞ Post a challenge</CCText>
          </Pressable>
        </View>
      ) : null}
      <View style={styles.composerBar}>
        {/* "+" composer — opens the poll/challenge menu. Any member can post a
            challenge; poll creation is admin-only (gated inside the menu). */}
        <Pressable
          testID="chat-plus"
          style={styles.plusButton}
          onPress={() => setComposerMenuOpen((o) => !o)}
        >
          <CCText style={{ fontSize: 22, lineHeight: 24, color: "#3DAB9E" }}>+</CCText>
        </Pressable>
        <TextInput
          testID="chat-input"
          value={text}
          onChangeText={setText}
          onSubmitEditing={onSend}
          placeholder="Message the club"
          placeholderTextColor="#A8A7A6"
          style={[
            styles.textInput,
            { backgroundColor: isWide ? "#FAF6EB" : "#FFFFFF" },
            Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : null,
          ]}
        />
        <Pressable testID="chat-send" onPress={onSend} style={styles.sendButton}>
          <Svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#4D3F37"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <Path d="m22 2-7 20-4-9-9-4Z" />
            <Path d="M22 2 11 13" />
          </Svg>
        </Pressable>
      </View>
    </View>
  );
}
